import { useMemo } from "react";
import { useApi, useApiMutation } from "./use-api";

export type EvergreenMode =
  | "scheduled_replay"
  | "just_in_time"
  | "on_demand"
  | "automated_series";

export interface ScheduledReplayConfig {
  dayOfWeek: number[];
  timeOfDay: string;
  timezone: string;
}

export interface JustInTimeConfig {
  intervalMinutes: number;
  availableHoursStart: number;
  availableHoursEnd: number;
}

export interface AutomatedSeriesConfig {
  days: number[];
  timeOfDay: string;
  timezone: string;
}

export type EvergreenConfig =
  | ScheduledReplayConfig
  | JustInTimeConfig
  | AutomatedSeriesConfig
  | Record<string, never>;

export interface EvergreenScheduleRecord {
  id?: string;
  webinarId: string;
  recordingId?: string | null;
  mode: EvergreenMode;
  config: EvergreenConfig;
  simulatedAttendeeCount: number;
  simulatedAttendeeRange?: { min: number; max: number } | null;
  isActive?: boolean;
  createdAt?: string;
}

export interface UpcomingEvergreenSession {
  startsAt: string;
  label: string;
}

export interface EvergreenSchedulePreview {
  mode: EvergreenMode;
  nextStartAt: string;
  isAvailableNow: boolean;
  upcomingSessions: UpcomingEvergreenSession[];
}

export interface EvergreenScheduleViewModel extends EvergreenScheduleRecord {
  nextStartAt: string | null;
  isAvailableNow: boolean;
  upcomingSessions: UpcomingEvergreenSession[];
}

export function useEvergreenSchedule(webinarId: string | undefined) {
  const scheduleQuery = useApi<EvergreenScheduleRecord | null>(
    webinarId ? `/v1/webinars/${webinarId}/evergreen/schedule` : "",
  );
  const previewQuery = useApi<EvergreenSchedulePreview>(
    webinarId ? `/v1/webinars/${webinarId}/evergreen/next-session?count=5` : "",
    { enabled: Boolean(webinarId && scheduleQuery.data) },
  );

  const data = useMemo<EvergreenScheduleViewModel | null>(() => {
    if (!scheduleQuery.data) {
      return null;
    }

    return {
      ...scheduleQuery.data,
      nextStartAt: previewQuery.data?.nextStartAt ?? null,
      isAvailableNow: previewQuery.data?.isAvailableNow ?? false,
      upcomingSessions: previewQuery.data?.upcomingSessions ?? [],
    };
  }, [previewQuery.data, scheduleQuery.data]);

  return {
    data,
    isLoading: scheduleQuery.isLoading || (Boolean(scheduleQuery.data) && previewQuery.isLoading),
    error: scheduleQuery.error ?? previewQuery.error,
    refetch: () => {
      scheduleQuery.refetch();
      previewQuery.refetch();
    },
  };
}

export function useSaveEvergreenSchedule(webinarId: string | undefined) {
  return useApiMutation<
    Omit<EvergreenScheduleRecord, "webinarId" | "id" | "createdAt">,
    { data: EvergreenScheduleRecord }
  >(
    webinarId ? `/v1/webinars/${webinarId}/evergreen/schedule` : "",
    "POST",
  );
}
